<template>
    <div
        ref="app" id="cz2"
        :class="[
            'cz2',
            `left-0 top-0 w-screen bg-white app-height transition-opacity duration-200`,
            loaded ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
            { 'remove-all-outlines': hideOutlines },
            { 'cz3--app3': appVersion === '3' },
        ]"
        @keydown="() => { hideOutlines = false; }"
        @mousedown="() => { hideOutlines = true; }"
    >
        <div class="h-full w-full relative">
            <customizer-new v-if="appVersion === '3'" />

            <template v-else>
                <top-nav class="hidden lg:!flex h-24" />
                <div
                    class="flex w-full"
                    :class="isDesktop.value ? 'app-height' : 'h-full'">
                    <div class="w-full h-full flex flex-col justify-start relative">
                        <renderer class="flex-1" />
                        <mobile-panel v-if="!isDesktop.value" />
                    </div>
                    <desktop-panel v-if="isDesktop.value"/>
                </div>
            </template>

            <transition name="cz2__fade">
                <popup-leaving v-if="showExitConfirmation"></popup-leaving>
            </transition>

            <transition name="cz2__fade">
                <popup-done v-if="showDoneConfirmation"></popup-done>
            </transition>

            <!-- Confirm switch to group personalization experience -->
            <transition name="cz2__fade">
                <popup-group v-if="showGroupConfirmation"></popup-group>
            </transition>

            <!-- Confirm switch to individual experience -->
            <transition name="cz2__fade">
                <popup-group
                    v-if="showIndividualConfirmation"
                    switchTo="individual"
                ></popup-group>
            </transition>

            <transition name="cz2__fade">
                <popup-generic-notify
                    v-if="showGenericNotify"
                ></popup-generic-notify>
            </transition>

            <transition name="cz2__fade">
                <popup-generic-confirm
                    v-if="showGenericConfirm"
                ></popup-generic-confirm>
            </transition>

            <transition name="cz2__fade">
                <popup-tos v-if="showTosConfirmation"></popup-tos>
            </transition>

            <div v-if="globalBusy" class="absolute left-0 top-0 w-full h-full z-50 bg-opacity-60 cursor-wait">
                <div class="absolute left-0 right-0 w-full h-full flex justify-center items-center bg-stone-500 bg-opacity-50 z-50">
                    <div class="cz2__modal-popup__body rounded bg-white text-center pb-12 pt-16 relative px-8">
                        <div>
                            <div class="cz2__modal-popup__content" tabindex="0">
                                <div class="cz2__modal-popup__header text-base lg:text-2xl lg:tracking-loose !px-0 !text-center">
                                    {{$t('SAVING')}}
                                </div>

                                <div v-if="isGroupOrder" class="cz2__modal-popup__body-copy text-lg !mb-0 !px-0 !text-center">
                                    {{$t('SAVING_GROUP')}}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    @import "style/variables.scss";
    @import "style/fonts.scss";

    .app-height {
        min-height: var(--app-height);
        height: var(--app-height);
    }

    .remove-all-outlines {
        *, a, button {
            outline: none !important;
        }

        :focus {
            outline: none !important;
        }
    }

    #cz2 {
        z-index: 10000;
        width: 100vw;
        min-height: var(--app-height);
        height: var(--app-height);
        overflow: hidden;

        background: #fff;
    }

    html.cz2__overlay-frame,
    html.cz2__overlay-frame > body {
        height: 100vh;
        height: var(--app-height);
        overflow: hidden;
    }

    .cz2__overlay-lock > *:not(.cz2):not(#tasks-container):not(.driver-popover):not(.driver-active-element):not(.driver-overlay) {
        display: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    #cz2 {
        @import 'vue-multiselect/dist/vue-multiselect.min';
    }

    // Fix driverjs adding overflow.
    :not(body):has(> .driver-active-element) {
        overflow: unset !important;
    }
</style>

<script>
    import { useMediaQuery } from '@vueuse/core';
    import '@netless/canvas-polyfill';
    import interact from 'interactjs';

    import { mapActions, mapState } from 'vuex';
    import Renderer from './components/Renderer.vue';
    import { DESKTOP_BREAKPOINT } from './constants';

    import PopupLeaving from './components/popups/PopupLeaving.vue';
    import PopupDone from './components/popups/PopupDone.vue';
    import PopupGroup from './components/popups/PopupGroup.vue';
    import PopupGenericNotify from './components/popups/PopupGenericNotify.vue';
    import PopupGenericConfirm from './components/popups/PopupGenericConfirm.vue';
    import PopupTos from './components/popups/PopupTos.vue';
    import DesktopPanel from './components/panels/desktop/DesktopPanel.vue';
    import MobilePanel from './components/panels/mobile/MobilePanel.vue';
    import TopNav from './components/navigation/TopNav.vue';

    import * as Tours from './tours';
    import { fillEmptyDivs } from './utils';

    import CustomizerNew from './components/V2/CustomizerNew.vue';

    export default {
        props: {
            options: {
                type: Object,
                default: () => {},
            },
        },
        components: {
            Renderer,
            PopupLeaving,
            PopupDone,
            PopupGroup,
            PopupGenericNotify,
            PopupGenericConfirm,
            PopupTos,
            TopNav,
            MobilePanel,
            DesktopPanel,
            CustomizerNew,
        },
        data() {
            return {
                loaded: false,
                hideOutlines: true,
            };
        },
        computed: {
            ...mapState({
                brand: (state) => state.customizer.viewController?.blueprint?.custom['v-logo-type'],
                globalBusy: (state) => state.globalBusy, /* Is the UX blocked? */
                showExitConfirmation: (state) => state.showExitConfirmation,
                showDoneConfirmation: (state) => state.showDoneConfirmation,
                showGroupConfirmation: (state) => state.showGroupConfirmation,
                showIndividualConfirmation: (state) => state.showIndividualConfirmation,
                showGenericNotify: (state) => state.showGenericNotify,
                showGenericConfirm: (state) => state.showGenericConfirm,
                showTosConfirmation: (state) => state.showTosConfirmation,
                isGroupOrder: (state) => state.isGroupOrder,
                selfDestruct: (state) => state.selfDestruct, /* Shortcut to self-destruct flag. */
                globalLoaded: (state) => state.globalLoaded,
            }),

            appVersion() {
                return this.$store.state.appVersion;
            },

            isDesktop() {
                return useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
            },
        },
        watch: {
            selfDestruct(v) { /** Begin self-destruct sequence. */
                if (!v) { return; }

                this.loaded = false;

                setTimeout(() => {
                    this.$emit('selfDestruct');
                }, 500);
            },

            globalLoaded() {
                // this.showIntroTour();
            },
        },
        mounted() {
            this.$store.dispatch('setTranslationsRoot', () => this.$root.$i18n);

            const root = document.getElementsByTagName('html')[0];
            root.classList.add(`${this.brand}-theme`);

            document.querySelectorAll('div:empty').forEach((div) => {
                div.innerHTML = '<!-- placeholder -->';
            });

            interact.addDocument(window.document, {
                events: { passive: false },
            });

            this.saveOptions();

            this.loadProductData();

            // Reset scroll position.
            this.oldBodyScroll = document.body.scrollTop || document.documentElement.scrollTop;

            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;

            // document.body.style.height = this.lockHeight ? `${this.lockHeight}px` : `${this.$refs.app.offsetHeight}px`;

            document.body.classList.add('cz2__overlay-lock');
            document.documentElement.classList.add('cz2__overlay-frame');

            this.overrideFontSize();

            // Listen to resize.
            window.addEventListener('resize', this.resize);

            // And resize right away.
            this.$nextTick(() => {
                this.resize();
            });

            // Because the scrolling state may have changed now with the body height adjusted, reevaluate sizing a moment later.
            this.heightTimer = setInterval(this.resize, 100);

            this.excludesTimer = setInterval(this.updateSizeExcludes, 100);
            fillEmptyDivs();
        },

        beforeDestroy() {
            const root = document.getElementsByTagName('html')[0];
            root.classList.remove(`${this.brand}-theme`);
            document.body.classList.remove('cz2__overlay-lock');
            document.documentElement.classList.remove('cz2__overlay-frame');
            document.body.style.height = 'auto';

            window.removeEventListener('resize', this.resize);

            if (this.oldBodyScroll) {
                document.body.scrollTop = this.oldBodyScroll;
                document.documentElement.scrollTop = this.oldBodyScroll;
            }

            if (this.heightTimer) {
                clearInterval(this.heightTimer);
            }

            if (this.excludesTimer) {
                clearInterval(this.excludesTimer);
            }
        },

        methods: {
            ...mapActions({
                setOptions: 'setOptions',
                setCustomizerOptions: 'setCustomizerOptions',
                setEcommerceOptions: 'setEcommerceOptions',
                setTranslations: 'setTranslations',
            }),

            loadProductData() {
                this.$store.dispatch('load', {
                    options: this.options,
                });

                this.loaded = true;
            },

            saveOptions() {
                this.setTranslations((v) => this.$t(v));
                this.setOptions(this.options);
                this.setCustomizerOptions(this.options);
                this.setEcommerceOptions(this.options);
            },

            resize() {
                const h = window.innerHeight * (document.body.clientWidth / window.innerWidth);

                const selected = document.activeElement;

                if (selected instanceof HTMLInputElement) {
                    // Ignore size changes while something has a focus.
                    return;
                }

                if (this.lockHeight !== h) {
                    this.lockHeight = h;

                    this.$refs.app.style.setProperty('--app-height', `${this.lockHeight}px`);

                    this.$store.dispatch('setHeight', this.lockHeight);

                    document.body.style.height = `${this.lockHeight}px`;
                }
            },

            updateSizeExcludes() {
                const parent = this.$refs.app;

                if (parent) {
                    const excludes = Array.from(parent.querySelectorAll('.cz2-renderer-exclude'));

                    let excludeHeight = 0;
                    let transitioning = false;

                    excludes.forEach((el) => {
                        excludeHeight += el.offsetHeight;

                        const animations = el.getAnimations();
                        // eslint-disable-next-line no-undef
                        const transitions = animations.filter((anim) => anim instanceof CSSTransition);

                        if (transitions.length > 0) {
                            transitioning = true;
                        }
                    });

                    // Only if all transitions settled.
                    if (!transitioning && this.lastExclude !== excludeHeight) {
                        this.lastExclude = excludeHeight;

                        this.$refs.app.style.setProperty('--exclude-height', `${excludeHeight}px`);
                    }
                }
            },

            /**
             * Override font size applied to the HTML tag
             */
            overrideFontSize() {
                const htmlTag = document.documentElement;

                htmlTag.style.fontSize = '100%';
                document.body.style.fontSize = '100%';
            },

            /**
             * Show intro tour.
             */
            showIntroTour() {
                // TODO: Load timing hack...
                setTimeout(() => {
                    this.$store.dispatch('setCurrentTour', Tours.intro());
                }, 250);
            },
        },
    };
</script>
